<template>
  <div class="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-6">
    <h2 class="tw-text-lg tw-font-semibold tw-text-slate-900 tw-mb-4">
      Customer Details
    </h2>

    <form
      ref="paymentForm"
      @submit.prevent="onSubmit"
    >
      <!-- Customer name -->
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
        <div>
          <label
            for="first_name"
            class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
          >
            First name <span class="tw-text-red-500">*</span>
          </label>
          <input
            id="first_name"
            v-model="formData.first_name"
            type="text"
            name="first_name"
            placeholder="John"
            class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
            required
          >
        </div>
        <div>
          <label
            for="last_name"
            class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
          >
            Last name <span class="tw-text-red-500">*</span>
          </label>
          <input
            id="last_name"
            v-model="formData.last_name"
            type="text"
            name="last_name"
            placeholder="Smith"
            class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
            required
          >
        </div>
      </div>

      <!-- Contact details -->
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
        <div>
          <label
            for="email"
            class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
          >
            Email <span class="tw-text-red-500">*</span>
          </label>
          <input
            id="email"
            v-model="formData.email"
            type="email"
            name="email"
            placeholder="john.smith@example.com"
            class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
            required
          >
        </div>
        <div>
          <label
            for="phone"
            class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
          >
            Phone <span class="tw-text-red-500">*</span>
          </label>
          <input
            id="phone"
            v-model="formData.phone"
            type="tel"
            name="phone"
            placeholder="07123 456789"
            class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
            required
          >
        </div>
      </div>

      <!-- Billing address -->
      <h3 class="tw-text-base tw-font-semibold tw-text-slate-900 tw-mt-6 tw-mb-3">
        Billing Address
      </h3>
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
        <div class="md:tw-col-span-2">
          <label
            for="address_one"
            class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
          >
            Address Line 1 <span class="tw-text-red-500">*</span>
          </label>
          <input
            id="address_one"
            v-model="formData.billing_address_one"
            type="text"
            name="billing_address_one"
            placeholder="123 Main Street"
            class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
            required
          >
        </div>
        <div class="md:tw-col-span-2">
          <label
            for="address_two"
            class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
          >
            Address Line 2
          </label>
          <input
            id="address_two"
            v-model="formData.billing_address_two"
            type="text"
            name="billing_address_two"
            placeholder="Apartment, suite, etc. (optional)"
            class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
          >
        </div>
        <div>
          <label
            for="city"
            class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
          >
            City <span class="tw-text-red-500">*</span>
          </label>
          <input
            id="city"
            v-model="formData.billing_address_city"
            type="text"
            name="billing_city"
            placeholder="London"
            class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
            required
          >
        </div>
        <div>
          <label
            for="postcode"
            class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
          >
            Postcode <span class="tw-text-red-500">*</span>
          </label>
          <input
            id="postcode"
            v-model="formData.billing_address_postcode"
            type="text"
            name="billing_postcode"
            placeholder="SW1A 1AA"
            class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
            required
          >
        </div>
      </div>

      <!-- Super Payments Embedded Checkout -->
      <div class="tw-mt-6">
        <h3 class="tw-text-base tw-font-semibold tw-text-slate-900 tw-mb-3">
          Payment
        </h3>
        <div
          v-if="superLoading"
          class="tw-text-center tw-py-8"
        >
          <div class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center">
            <span class="tw-h-6 tw-w-6 tw-border-2 tw-border-blue-500 tw-border-t-transparent tw-rounded-full tw-animate-spin" />
          </div>
          <p class="tw-mt-2 tw-text-sm tw-text-slate-500">
            Loading payment form...
          </p>
        </div>
        <div
          v-else-if="superError"
          class="tw-bg-red-50 tw-border tw-border-red-200 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded-md tw-text-sm"
        >
          {{ superError }}
        </div>
        <super-checkout
          v-else-if="superSessionToken"
          :amount="amountInPence"
          :checkout-session-token="superSessionToken"
          :theme="superTheme"
        />
      </div>

      <!-- Terms and Conditions -->
      <div class="tw-mt-4">
        <div
          class="tw-rounded-lg tw-border tw-p-4 tw-transition-colors"
          :class="termsAccepted ? 'tw-border-emerald-200 tw-bg-emerald-50' : 'tw-border-slate-200 tw-bg-slate-50'"
        >
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
            <div class="tw-flex tw-items-center tw-gap-3">
              <div
                class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full"
                :class="termsAccepted ? 'tw-bg-emerald-100' : 'tw-bg-slate-200'"
              >
                <svg
                  v-if="termsAccepted"
                  class="tw-h-4 tw-w-4 tw-text-emerald-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <svg
                  v-else
                  class="tw-h-4 tw-w-4 tw-text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <p
                  class="tw-text-sm tw-font-medium"
                  :class="termsAccepted ? 'tw-text-emerald-800' : 'tw-text-slate-700'"
                >
                  {{ termsAccepted ? 'Terms and conditions accepted' : 'Terms and conditions' }}
                </p>
                <p
                  v-if="!termsAccepted"
                  class="tw-text-xs tw-text-slate-500"
                >
                  Please read and accept before continuing
                </p>
              </div>
            </div>
            <button
              type="button"
              class="tw-inline-flex tw-items-center tw-rounded-md tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 tw-transition-colors"
              :class="termsAccepted
                ? 'tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 focus:tw-ring-slate-500'
                : 'tw-border tw-border-transparent tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 focus:tw-ring-blue-500'"
              @click="showTermsModal = true"
            >
              {{ termsAccepted ? 'Review' : 'Accept Terms' }}
            </button>
          </div>
        </div>
      </div>

      <TermsModal
        :show="showTermsModal"
        :terms-url="termsUrl"
        @close="showTermsModal = false"
        @accept="termsAccepted = true"
      />

      <!-- Submit button -->
      <button
        type="submit"
        class="tw-mt-6 tw-w-full tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-px-4 tw-py-3 tw-rounded-md tw-font-medium tw-text-sm tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
        :disabled="submitting || !superSessionToken || !termsAccepted"
      >
        <span v-if="submitting">Processing payment…</span>
        <span v-else>Complete Payment ({{ formatCurrency(amount) }})</span>
      </button>
    </form>
  </div>
</template>

<script>
import { ref, computed, onMounted, inject } from 'vue'
import TermsModal from '../../quote-payment/components/TermsModal.vue'

export default {
  name: 'PaymentForm',
  components: {
    TermsModal
  },
  props: {
    reference: {
      type: String,
      required: true
    },
    amount: {
      type: Number,
      required: true
    }
  },
  emits: ['payment-success', 'payment-error', 'payment-confirm'],
  setup(props, { emit }) {
    const coreService = inject('coreService')

    const paymentForm = ref(null)
    const submitting = ref(false)

    // Super Payments state
    const superSessionToken = ref('')
    const superSessionId = ref('')
    const transactionToken = ref('')
    const superLoading = ref(false)
    const superError = ref('')
    const superScriptLoaded = ref(false)

    const termsAccepted = ref(false)
    const showTermsModal = ref(false)

    const termsUrl = computed(() => {
      const fromBackendConfig = (typeof backendConfig !== 'undefined' && backendConfig?.pageData?.termsAndConditionsUrl)
        ? backendConfig.pageData.termsAndConditionsUrl
        : ''
      return fromBackendConfig || process.env.VUE_APP_TERMS_URL || 'https://www.quickslide.co.uk/terms-conditions/'
    })

    const formData = ref({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      billing_address_one: '',
      billing_address_two: '',
      billing_address_city: '',
      billing_address_postcode: '',
    })

    // Convert amount to pence for Super Payments
    const amountInPence = computed(() => {
      return Math.round(props.amount * 100)
    })

    // Theme configuration for Super Payments
    const superTheme = computed(() => {
      return JSON.stringify({
        hideSuperApp: false,
        container: {
          backgroundColor: '#ffffff',
          borderRadius: '8px',
        },
      })
    })

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(value || 0)
    }

    // Load Super Payments script dynamically
    const loadSuperPaymentsScript = () => {
      return new Promise((resolve, reject) => {
        if (superScriptLoaded.value || window.superCheckout) {
          superScriptLoaded.value = true
          resolve()
          return
        }

        const script = document.createElement('script')
        script.src = 'https://cdn.superpayments.com/js/payment.js'
        script.async = true
        script.onload = () => {
          superScriptLoaded.value = true
          resolve()
        }
        script.onerror = () => reject(new Error('Failed to load Super Payments script'))
        document.head.appendChild(script)
      })
    }

    // Initialize Super Payments
    const initSuperPayments = async () => {
      superLoading.value = true
      superError.value = ''

      try {
        await loadSuperPaymentsScript()

        // Call API to get Super Payments session
        const response = await coreService.initAdhocPayment({
          reference: props.reference,
          amount: props.amount
        })

        superSessionToken.value = response.data.checkoutSessionToken
        superSessionId.value = response.data.checkoutSessionId
        transactionToken.value = response.data.transaction
      } catch (err) {
        console.error('Failed to initialize Super Payments:', err)
        superError.value = err?.response?.data?.message || err?.message || 'Failed to load payment form'
        emit('payment-error', superError.value)
      } finally {
        superLoading.value = false
      }
    }

    onMounted(async () => {
      await initSuperPayments()
    })

    const onSubmit = async () => {
      if (!coreService) {
        emit('payment-error', 'Payment service not available')
        return
      }

      submitting.value = true

      try {
        // Step 1: Submit to Super Payments
        if (!window.superCheckout) {
          throw new Error('Super Payments not loaded')
        }

        const result = await window.superCheckout.submit()

        if (result.status === 'FAILURE') {
          const errorMessage = result.errorMessage || 
            'Payment failed. No money has been taken. Please try again.'
          throw new Error(errorMessage)
        }

        if (result.status !== 'SUCCESS') {
          throw new Error('Payment was not completed')
        }

        // Step 2: Call proceed endpoint with transaction token and customer details
        const response = await coreService.proceedAdhocPayment(transactionToken.value, {
          email: formData.value.email,
          phone: formData.value.phone,
          name: `${formData.value.first_name} ${formData.value.last_name}`.trim(),
        })

        // Step 3: Handle redirect URL or direct success
        if (response.data?.redirectUrl) {
          const url = new URL(response.data.redirectUrl, window.location.origin)
          const token = url.searchParams.get('token')
          if (token) {
            emit('payment-confirm', token)
          } else {
            window.location.href = response.data.redirectUrl
          }
        } else {
          emit('payment-success', response?.data)
        }
      } catch (err) {
        const errorMsg = err?.response?.data?.message || err?.message || 'Payment failed'
        emit('payment-error', errorMsg)
      } finally {
        submitting.value = false
      }
    }

    return {
      paymentForm,
      formData,
      submitting,
      superSessionToken,
      superLoading,
      superError,
      superTheme,
      amountInPence,
      formatCurrency,
      termsAccepted,
      showTermsModal,
      termsUrl,
      onSubmit
    }
  }
}
</script>

<style scoped></style>
